import { useEffect, useState, useCallback } from 'react';
import { AnchorAPI } from '../modules/AnchorAPI';

export enum ImportPollingStatus {
  IDLE,
  IN_PROGRESS,
  COMPLETE,
  NOT_FOUND,
}

export type ImportStatus = {
  status: ImportPollingStatus;
  percentageComplete: number;
  totalEpisodes: number;
  errors: {
    rssImportItemId: number;
    state: string;
    kind: string;
    title: string | null;
  }[];
};

const initalState = {
  status: ImportPollingStatus.IDLE,
  percentageComplete: 0,
  totalEpisodes: 0,
  errors: [],
};

const MAX_FAILURES = 3;

export const usePollForImportStatus = (webStationId: string | null) => {
  const [importStatus, setImportStatus] = useState<ImportStatus>(initalState);

  const pollForImportStatus = useCallback(() => {
    if (webStationId) {
      let makeNextCall = true;
      let failureCount = 0;

      setImportStatus(initalState);
      const interval = setInterval(async () => {
        try {
          if (!makeNextCall) return;

          makeNextCall = false;
          const {
            state,
            percentageComplete,
            items,
          } = await AnchorAPI.fetchImportStatus({
            stationId: webStationId,
          });
          makeNextCall = true;

          if (state === 'complete') {
            clearInterval(interval);
            setImportStatus({
              status: ImportPollingStatus.COMPLETE,
              percentageComplete,
              totalEpisodes: items.length && items.length - 1, // exclude channel
              errors: items.filter(
                ({ state: itemState, kind }: { state: string; kind: string }) =>
                  itemState === 'failed' && kind === 'item'
              ),
            });
          } else {
            setImportStatus({
              status: ImportPollingStatus.IN_PROGRESS,
              percentageComplete,
              totalEpisodes: items.length && items.length - 1, // exclude channel
              errors: [],
            });
          }
        } catch (err) {
          if (
            err.response &&
            (err.response.status === 403 || err.response.status === 404)
          ) {
            clearInterval(interval);
            setImportStatus(status => ({
              ...status,
              ...{ status: ImportPollingStatus.NOT_FOUND },
            }));
          } else {
            makeNextCall = true;
            failureCount = failureCount + 1;
            if (failureCount >= MAX_FAILURES) {
              clearInterval(interval);
              setImportStatus(prevImportStatus => {
                return {
                  ...prevImportStatus,
                  status: ImportPollingStatus.NOT_FOUND,
                };
              });
            }
          }
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [webStationId]);

  /** by default, polling will start on mount. we have an optional argument
   * `disablePolling` that will prevent polling from starting. the use case here
   * are for podcasts that are _not_ imported. if they aren't imported, there
   * is no reason to poll for the import status.
   */
  useEffect(() => {
    if (webStationId) {
      pollForImportStatus();
    }
  }, [webStationId, pollForImportStatus]);

  return { importStatus, pollForImportStatus };
};
